import { useCallback, useEffect, useRef, useState } from 'react'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import { fetchConnectSubjects } from '../api/hrcClient'
import type { HrcConnectSubject } from '../types'

export type ConnectSubjectsLoad =
  | { phase: 'loading' }
  | { phase: 'ready'; subjects: HrcConnectSubject[] }
  | { phase: 'failed' }

/**
 * The roster from `GET /hrc/connect/subjects`, loaded once, with a refresh for after a
 * connection is saved.
 *
 * A refresh that fails keeps the roster on screen: the connection it followed genuinely
 * succeeded, so replacing a working list with an error over a stale one would be the worse
 * answer.
 */
export function useConnectSubjects(): { load: ConnectSubjectsLoad; refresh: () => void } {
  const client = useFamilyPortalApiClient()
  const [load, setLoad] = useState<ConnectSubjectsLoad>({ phase: 'loading' })
  const mounted = useRef(true)

  // The client is rebuilt on every render, so depending on its identity re-runs this on
  // every state change -- each fetch setting state, which fetches again. Read it through
  // a ref and load exactly once, the same way useHrcConnect avoids the dependency churn.
  const clientRef = useRef(client)
  clientRef.current = client

  const fetchRoster = useCallback(async (initial: boolean) => {
    try {
      const subjects = await fetchConnectSubjects(clientRef.current)
      if (mounted.current) setLoad({ phase: 'ready', subjects })
    } catch {
      if (mounted.current && initial) setLoad({ phase: 'failed' })
    }
  }, [])

  // Stable, so a card can list it in its effect's deps honestly instead of suppressing the
  // lint rule. An inline arrow is a new function every render, which would make a
  // correctly-declared effect re-run on each one.
  const refresh = useCallback(() => void fetchRoster(false), [fetchRoster])

  useEffect(() => {
    mounted.current = true
    void fetchRoster(true)
    return () => {
      mounted.current = false
    }
  }, [fetchRoster])

  return { load, refresh }
}
